import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useDataViewerJobs, useRunQueryJob, useExportQueryJob } from '../hooks/useDataViewer.js';
import {
  cellToString,
  splitFilterConditionInputs,
  type FilterCondition,
} from '../lib/data-viewer-filter.js';
import { Button } from '../components/ui/Button/index.js';
import { Select } from '../components/ui/Select/index.js';
import { ErrorState } from '../components/feedback/ErrorState/index.js';
import {
  FilterPanel,
  buildInitialConditions,
} from '../components/features/data-viewer/FilterPanel/index.js';
import type { QueryResult } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business クエリ実行ページ。ジョブ選択→実行→結果テーブル表示→CSVエクスポート。
 *   cortex_admin_data_viewer_permissions.allowedEmails に含まれるユーザーのみアクセス可（API側で 403 制御）
 * @graph-connects useDataViewerJobs [calls] ジョブ一覧取得フック
 * @graph-connects useRunQueryJob [calls] クエリ実行フック
 * @graph-connects useExportQueryJob [calls] CSVエクスポート用クエリ実行フック
 * @graph-connects FilterPanel [calls] 実行前フィルタ条件の入力UI
 * @graph-connects data-viewer-filter [calls] フィルタ条件の API 送信形への変換・セル値の文字列化
 */
export const Route = createFileRoute('/data-viewer')({
  component: DataViewerPage,
});

/** @graph-connects none */
function DataViewerPage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useDataViewerJobs();
  const { mutate: runJob, isPending, data: result, error: runError, reset } = useRunQueryJob();
  const {
    mutate: exportJob,
    isPending: isExporting,
    error: exportError,
    reset: resetExport,
  } = useExportQueryJob();
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const jobs = data?.jobs ?? [];
  const jobOptions = jobs.map((job) => ({
    value: job.id,
    label: job.description ? `${job.label}（${job.description}）` : job.label,
  }));
  const selectedJob = jobs.find((j) => j.id === selectedJobId);
  const filterableColumns = selectedJob?.filterableColumns ?? [];
  // 条件は API へ送る形に変換してから件数を見る。入力途中で値が空の条件は句にならないため、
  // 「フィルタが効いている」判定も変換後の件数で行う。
  // 登録時に DB 内カラムから選ばれた条件（table を持つもの）は集約前の行へ、
  // それ以外は実行結果へ条件がかかる。送信先が違うのでここで振り分ける
  const { filters: filterInputs, rawFilters: rawFilterInputs } =
    splitFilterConditionInputs(conditions);
  const isFiltered = filterInputs.length > 0 || rawFilterInputs.length > 0;

  const handleRun = () => {
    if (!selectedJobId) return;
    reset();
    resetExport();
    runJob({ jobId: selectedJobId, filters: filterInputs, rawFilters: rawFilterInputs });
  };

  const handleExportCsv = () => {
    if (!selectedJobId) return;
    const jobId = selectedJobId;
    exportJob(
      { jobId, filters: filterInputs, rawFilters: rawFilterInputs },
      {
        onSuccess: (exportResult) => {
          downloadCsv(exportResult, jobId);
        },
      },
    );
  };

  const downloadCsv = (queryResult: QueryResult, jobId: string) => {
    const { columns, rows } = queryResult;
    const csvLines: string[] = [columns.map(escapeCsvValue).join(',')];
    for (const row of rows) {
      csvLines.push(columns.map((col) => escapeCsvValue(cellToString(row[col]))).join(','));
    }
    const bom = '﻿';
    const blob = new Blob([bom + csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${jobId}_${formatDateForFilename()}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="animate-content-reveal">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dataViewer.title')}
          </h1>
        </div>
        <div className="glass-card p-6 rounded-xl">
          <div className="h-10 skeleton-glass rounded w-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={t('common.error')}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ページヘッダー */}
      <div className="animate-content-reveal">
        <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
          {t('dataViewer.title')}
        </h1>
      </div>

      {/* クエリ選択・実行 */}
      <div className="glass-card p-6 rounded-xl space-y-4 animate-content-reveal">
        {jobs.length === 0 ? (
          <p className="text-[var(--color-text-muted)]">{t('dataViewer.noJobs')}</p>
        ) : (
          <>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <Select
                  label={t('dataViewer.selectJob')}
                  placeholder={t('dataViewer.selectJobPlaceholder')}
                  options={jobOptions}
                  value={selectedJobId}
                  onChange={(e) => {
                    const nextJobId = e.target.value;
                    setSelectedJobId(nextJobId);
                    reset();
                    resetExport();
                    // ジョブごとにフィルタ対象カラムが違うので、選び直したら条件を作り直す
                    const nextJob = jobs.find((job) => job.id === nextJobId);
                    setConditions(buildInitialConditions(nextJob?.filterableColumns ?? []));
                  }}
                  fullWidth
                />
              </div>
              <Button
                variant="primary"
                onClick={handleRun}
                disabled={!selectedJobId || isPending}
                loading={isPending}
                className="shrink-0"
              >
                {isPending ? t('dataViewer.running') : t('dataViewer.run')}
              </Button>
            </div>

            {selectedJob?.description && (
              <p className="text-sm text-[var(--color-text-secondary)]">
                {selectedJob.description}
              </p>
            )}
          </>
        )}
      </div>

      {/* フィルタ（実行前に条件を指定して SQL の WHERE で絞る） */}
      {selectedJobId && (
        <FilterPanel
          filterableColumns={filterableColumns}
          conditions={conditions}
          onChange={setConditions}
        />
      )}

      {/* エラー表示 */}
      {runError && (
        <div className="glass-card p-4 rounded-xl border border-[var(--color-error-muted)] animate-fade-in">
          <p className="text-sm text-[var(--color-error)]">{t('dataViewer.runError')}</p>
        </div>
      )}

      {/* 実行結果 */}
      {result && (
        <div className="glass-card p-6 rounded-xl space-y-4 animate-fade-in">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[var(--color-text-secondary)]">
              {isFiltered
                ? t('dataViewer.filteredResultCount', { count: result.total })
                : t('dataViewer.resultCount', { count: result.total })}
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExportCsv}
              disabled={isExporting}
              loading={isExporting}
              leftIcon={
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              }
            >
              {t('dataViewer.exportCsv')}
            </Button>
          </div>

          {/* エクスポート失敗（フィルタ値が API の検証に落ちたケースを含む）は理由まで出す */}
          {exportError && (
            <p className="text-sm text-[var(--color-error)]">
              {t('dataViewer.exportError', { reason: exportError.message })}
            </p>
          )}

          {result.rows.length === 0 ? (
            <p className="text-[var(--color-text-muted)] text-sm">
              {isFiltered ? t('dataViewer.noFilteredResult') : t('dataViewer.noResult')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-[var(--color-border)]">
                    {result.columns.map((col) => (
                      <th
                        key={col}
                        className="px-3 py-2 text-[var(--color-text-secondary)] font-medium whitespace-nowrap"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      {result.columns.map((col) => (
                        <td
                          key={col}
                          className="px-3 py-2 text-[var(--color-text-primary)] whitespace-nowrap max-w-xs truncate"
                          title={cellToString(row[col])}
                        >
                          {cellToString(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** @graph-connects none */
function escapeCsvValue(value: string): string {
  const needsQuoting = value.includes(',') || value.includes('"') || value.includes('\n');
  if (!needsQuoting) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/** @graph-connects none */
function formatDateForFilename(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${String(now.getFullYear())}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  );
}
